import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { alertEventBus } from 'src/core/events/Events';
import type { AlertRequestButton, AlertRequestEvent } from 'src/core/events/Events';
import Constants from 'src/core/utils/Constants';

/**
 * One pending dialog. `kind` decides the button count: a notice is an acknowledgement with a
 * single button, a confirmation asks a question and needs a way out as well.
 */
export interface AlertDialogRequest {
	id: number;
	kind: 'notice' | 'confirm';
	title: string;
	message: string | null;
	confirmText?: string;
	cancelText?: string;
	// Mapped from Alert's `confirmButtonStyle === 'destructive'` so the domain layer does not have
	// to know about react-native's AlertButton type.
	isDestructive?: boolean;
	onConfirm?: () => void;
	onCancel?: () => void;
	// Set instead of the fields above when the question has more than two answers, or two answers
	// that both do something. The dialog then draws exactly these, in this order, and `kind` and
	// the two labels are ignored.
	buttons?: AlertRequestButton[];
}

export type AlertDialogRequestInput = Omit<AlertDialogRequest, 'id'>;

interface AlertDialogState {
	// Only the first entry is on screen; the rest wait their turn. A single "current request" slot
	// would let a second notice overwrite the first, which is exactly what happens on web today --
	// two failing calls in a row and the visitor sees one of them, or neither.
	queue: AlertDialogRequest[];
}

const initialState: AlertDialogState = { queue: [] };

// Monotonic across the session on purpose: an id is never reused, so a click on a dialog that has
// already closed can never resolve the request that took its place.
let nextRequestId = 0;

export const useAlertDialogStore = create(
	combine(initialState, (set, get) => {
		/**
		 * Takes one request out of the queue, or returns undefined when it is not there any more.
		 * Both ways of closing a dialog go through this, because two things about it are
		 * load-bearing. The lookup is the double-click guard: the second click finds nothing left
		 * and does nothing at all. And the request leaves the queue before its callback runs,
		 * because that callback may raise a notice of its own (a failed save, say) -- which has to
		 * land behind the queue, not on top of an entry that is still in it.
		 */
		const take = (id: number): AlertDialogRequest | undefined => {
			const request = get().queue.find((entry) => entry.id === id);
			if (request === undefined) {
				return undefined;
			}

			set({ queue: get().queue.filter((entry) => entry.id !== id) });

			return request;
		};

		return {
			reset: () => set({ queue: [] }),

			enqueue: (request: AlertDialogRequestInput): number => {
				nextRequestId += 1;
				const id = nextRequestId;
				set({ queue: [...get().queue, { ...request, id }] });
				return id;
			},

			/**
			 * Closes one request and runs the action of the button at `index` exactly once. The
			 * counterpart of resolve for a request that carries its own button list; an index with no
			 * button behind it closes the dialog without running anything, which is what dismissing a
			 * list without a cancel entry does.
			 */
			pressButton: (id: number, index: number) => {
				take(id)?.buttons?.[index]?.onPress?.();
			},

			/** Closes one request and runs the matching callback exactly once. */
			resolve: (id: number, outcome: 'confirm' | 'cancel') => {
				const request = take(id);
				if (request === undefined) {
					return;
				}

				if (outcome === 'confirm') {
					request.onConfirm?.();
				} else {
					request.onCancel?.();
				}
			},
		};
	}),
);

/**
 * Bridge for notices raised outside the React tree. `errorHandler` sits in core and cannot import
 * this store, so it emits on the bus instead. Armed at import rather than from a component effect:
 * only the web dialog and the web AlertHelper import this module, so native never loads it, and a
 * notice raised before the first paint still ends up in the queue instead of vanishing.
 */
alertEventBus.on(Constants.eventAlertOnRequest, (...args: unknown[]) => {
	const event = args[0] as AlertRequestEvent | undefined;
	if (event === undefined) {
		return;
	}

	useAlertDialogStore.getState().enqueue({
		kind: event.kind ?? 'notice',
		title: event.title,
		message: event.message,
		confirmText: event.confirmText,
		cancelText: event.cancelText,
		isDestructive: event.isDestructive,
		onConfirm: event.onConfirm,
		onCancel: event.onCancel,
		buttons: event.buttons,
	});
});

export default useAlertDialogStore;
